import { authorizedFetch } from '../auth/session';

/**
 * What the generated client sends its requests through.
 *
 * Not the client's own `auth` option: minting the bearer is only half of what
 * `authorizedFetch` does. The other half is what happens when the API refuses
 * it — one replay with a freshly minted token, and an ended session when even
 * that is refused — and an `auth` callback cannot express either, because it
 * is consulted before the request and never sees the answer.
 *
 * Typed as `globalThis.fetch` by the client and called with a `Request`, so
 * the input is normalised here and unpacked back into a `RequestInit`: the
 * replay needs a body it can send twice, and a `Request`'s body is a stream
 * that the first attempt spends.
 */
export async function authorizedClientFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const request = new Request(input, init);

  // The method decides, not `request.body`. Asking the `Request` is not
  // portable — where the `body` getter is unimplemented it reads `undefined`,
  // not `null` — and a `=== null` test therefore fell through to `.blob()` on
  // a GET and attached the empty result, which Firefox rejects outright:
  // "HEAD or GET Request cannot have a body". Every read in the app failed,
  // starting with the `/admin/me` the authenticated layout loads on.
  const canHaveBody = request.method !== 'GET' && request.method !== 'HEAD';
  // `blob()` settles the stream in memory so the replay has something left to
  // send; every body this app sends is a form or a short JSON document. An
  // empty one is dropped: a bodyless POST — approve, reject — should stay
  // bodyless rather than acquire a zero-length one.
  const blob = canHaveBody ? await request.blob() : null;

  return authorizedFetch(request.url, {
    method: request.method,
    headers: request.headers,
    body: blob && blob.size > 0 ? blob : undefined,
    signal: request.signal,
    credentials: request.credentials,
  });
}
